import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class OrgErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to the browser console for debugging; a production logger
    // integration can replace this in the future.
    console.error("Organisation page error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="org-error"
          className="flex flex-col items-center justify-center p-8 text-center"
        >
          <h2 className="text-lg font-semibold text-destructive">
            Something went wrong
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Failed to load organisation data. Please try refreshing the page.
          </p>
          <button
            className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
            onClick={() => this.setState({ hasError: false, error: null })}
            aria-label="Try again - reload organisation data"
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
